import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo.jsx";
import { ChevronLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-brimi-cream">
      <Seo
        title="Privacy Policy — BrimiH&G"
        description="How BrimiH&G collects, uses and protects your data. Read our full privacy policy for the ICSE 2028 study app."
        path="/privacy"
      />
      <div className="max-w-3xl mx-auto px-5 pt-8 pb-24 md:pb-12">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-brimi-navyText/70 hover:text-brimi-navy" data-testid="back-home">
          <ChevronLeft size={16} /> Home
        </Link>
        <h1 className="text-4xl font-heading font-bold text-brimi-navyText mt-4">Privacy Policy</h1>
        <p className="text-sm text-brimi-navyText/60 mt-1">Last updated: {new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}</p>

        <div className="prose prose-brimi mt-8 text-brimi-navyText/85 leading-relaxed space-y-6 markdown-body">
          <section>
            <h2>1. Who we are</h2>
            <p>
              BrimiH&amp;G (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is an AI-powered study app for students of
              the Indian Certificate of Secondary Education (ICSE) Class IX and X preparing for the 2028 examination
              in History &amp; Civics and Geography. The service is operated at{" "}
              <a href="https://hg.brimi.in">https://hg.brimi.in</a> and via the BrimiH&amp;G Android / web apps.
            </p>
          </section>

          <section>
            <h2>2. Information we collect</h2>
            <ul>
              <li><strong>Account information:</strong> name, email address, and a password hash you create at signup.</li>
              <li><strong>Study activity:</strong> chapters you view, MCQ scores, mock test results, map-quiz progress, and chat session history with the AI tutor.</li>
              <li><strong>Payment metadata:</strong> when you subscribe via Razorpay, we receive a payment reference and plan type. We never store your card or banking details — those are handled entirely by Razorpay.</li>
              <li><strong>Technical data:</strong> IP address, browser type, device type, and basic logs needed to run the service securely.</li>
              <li><strong>Microphone audio (voice input):</strong> when you tap the mic in the AI tutor, your browser&apos;s built-in Web Speech API transcribes audio locally on your device. Audio is <em>never</em> uploaded to our servers — only the transcript text is sent.</li>
            </ul>
          </section>

          <section>
            <h2>3. How we use information</h2>
            <ul>
              <li>To provide the core service: AI notes, MCQs, tutor chat, mock tests, map work, progress tracking.</li>
              <li>To authenticate you and remember your session via a JWT.</li>
              <li>To process subscription payments via Razorpay.</li>
              <li>To improve the app and fix bugs (aggregated usage data only).</li>
              <li>To communicate important account or service notices.</li>
            </ul>
            <p>We do <strong>not</strong> sell or rent your personal data, ever.</p>
          </section>

          <section>
            <h2>4. Third-party services we use</h2>
            <ul>
              <li><strong>Anthropic (Claude Sonnet 4.5)</strong> — to generate chapter notes, MCQs and tutor replies. Your prompts and chat content are transmitted to Anthropic to produce the response. See Anthropic&apos;s privacy policy.</li>
              <li><strong>Razorpay</strong> — to process subscription payments. Razorpay collects payment information directly under its own policy.</li>
              <li><strong>Emergent</strong> — hosting and object storage for our backend.</li>
              <li><strong>MongoDB Atlas</strong> — database storage of account and study activity.</li>
            </ul>
          </section>

          <section>
            <h2>5. Data retention</h2>
            <p>
              We keep your account and study activity for as long as your account is active. If you delete your
              account, we delete your personal data within 30 days, except where we&apos;re required to retain it for
              legal or accounting reasons (e.g. tax records on payments).
            </p>
          </section>

          <section>
            <h2>6. Your rights</h2>
            <p>You can ask us to:</p>
            <ul>
              <li>Show you the personal data we hold about you.</li>
              <li>Correct anything that is wrong.</li>
              <li>Delete your account and personal data.</li>
              <li>Export your study activity.</li>
            </ul>
            <p>Email us at <a href="mailto:brajeshc56@gmail.com">brajeshc56@gmail.com</a> for any of the above.</p>
          </section>

          <section>
            <h2>7. Children&apos;s privacy</h2>
            <p>
              BrimiH&amp;G is designed for ICSE Class IX and X students — typically 13 to 16 years old. If you are
              under 13, please do not use the service. If you are 13–17, please use the service with the knowledge of
              a parent or guardian. We do not knowingly collect data from children under 13.
            </p>
          </section>

          <section>
            <h2>8. Security</h2>
            <p>
              Passwords are hashed with bcrypt. All traffic uses HTTPS. We follow industry-standard practices but no
              system is 100% secure — please use a strong, unique password.
            </p>
          </section>

          <section>
            <h2>9. Changes to this policy</h2>
            <p>
              We may update this policy from time to time. We&apos;ll show a notice in the app for any material changes.
              The &ldquo;Last updated&rdquo; date at the top tells you when this version was published.
            </p>
          </section>

          <section>
            <h2>10. Contact</h2>
            <p>
              Questions or requests? Email <a href="mailto:brajeshc56@gmail.com">brajeshc56@gmail.com</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
